<svelte:options runes={false} />
<script>
  // Posture mix (TASK-20260823-twap-strategies-tca PR-C2): a stacked bar of the filled quantity by
  // pricing posture — most-filled first — with a legend that names each segment and its share, so
  // the bar is never read by color alone. Each segment is a real button: focusable, labelled, and a
  // press pins its readout. Takes either a scorecard's postureMix or the tab's postureMixRollup.
  import { POSTURE_LABELS } from '../../../lib/twapPolicy.js';

  export let mix = [];          // [{ posture, postureName, qty, pct, parents? }]
  export let label = 'Posture mix';
  export let emptyText = 'No filled slices yet.';

  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  let active = null;
  let pinned = null;
  function togglePin(m) { pinned = pinned === m ? null : m; active = pinned ?? m; }
  function onKey(e, m) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePin(m); }
  }

  $: segments = (Array.isArray(mix) ? mix : []).filter((m) => isNum(m?.pct) && m.pct > 0);
  const nameOf = (m) => POSTURE_LABELS[m.posture] ?? m.postureName ?? `posture ${m.posture}`;
  const pctText = (m) => `${m.pct.toFixed(m.pct < 10 ? 1 : 0)} %`;
  function readout(m) {
    const parents = isNum(m.parents) ? ` across ${m.parents} parent${m.parents === 1 ? '' : 's'}` : '';
    return `${nameOf(m)}: ${pctText(m)} of filled qty${isNum(m.qty) ? ` (${Number(m.qty.toFixed(8))})` : ''}${parents}`;
  }
  $: summary = segments.length ? segments.map((m) => `${nameOf(m)} ${pctText(m)}`).join(', ') : emptyText;
</script>

<div class="pm">
  <span class="pm-label">{label} <span class="pm-sub">(share of filled qty)</span></span>
  {#if segments.length === 0}
    <p class="pm-empty">{emptyText}</p>
  {:else}
    <div class="pm-bar" role="group" aria-label={`${label}: ${summary}`}>
      {#each segments as m (m.posture)}
        <button type="button" class={`pm-seg pm-seg-${m.posture}`} style={`flex: ${m.pct} 0 0`}
          aria-label={readout(m)} aria-pressed={pinned === m}
          on:mouseenter={() => (active = m)} on:mouseleave={() => (active = pinned)}
          on:focus={() => (active = m)} on:blur={() => (active = pinned)}
          on:click={() => togglePin(m)} on:keydown={(e) => onKey(e, m)}>
          {#if m.pct >= 12}<span class="pm-seg-text num">{pctText(m)}</span>{/if}
        </button>
      {/each}
    </div>
    <ul class="pm-legend">
      {#each segments as m (m.posture)}
        <li class:pm-active={active === m}><span class={`pm-dot pm-seg-${m.posture}`} aria-hidden="true"></span>{nameOf(m)} <strong class="num">{pctText(m)}</strong></li>
      {/each}
    </ul>
    <p class="pm-readout num" aria-live="polite">{active ? readout(active) : ''}</p>
  {/if}
</div>

<style>
  .pm { display: flex; flex-direction: column; gap: 0.3rem; min-width: 0; }
  .num { font-variant-numeric: tabular-nums; }
  .pm-label { font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); text-transform: uppercase; letter-spacing: 0.04em; }
  .pm-sub { text-transform: none; letter-spacing: 0; color: var(--text-muted, #6a6b6d); }
  .pm-empty { margin: 0; font-size: var(--font-size-sm, 0.85rem); color: var(--text-muted, #6a6b6d); }
  .pm-bar { display: flex; height: 18px; border-radius: var(--radius-full, 999px); overflow: hidden; background: var(--bg-quaternary, #212226); }
  .pm-seg { appearance: none; border: none; padding: 0; margin: 0; min-width: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .pm-seg:focus-visible { outline: 2px solid var(--text-primary, #fff); outline-offset: -2px; }
  .pm-seg-text { font-size: var(--font-size-xs, 0.65rem); font-weight: var(--font-weight-semibold, 600); color: var(--bg-primary, #0a0b0d); }
  /* One hue per posture, mirroring MarketFootprint: aggressive (0) reads red → patient (4) reads green. */
  .pm-seg-0 { background: var(--color-danger, #ff3b3b); }
  .pm-seg-1 { background: var(--color-warning, #ffaa00); }
  .pm-seg-2 { background: var(--color-primary, #00d4ff); }
  .pm-seg-3 { background: var(--color-primary-dark, #0099cc); }
  .pm-seg-4 { background: var(--color-success, #00ff88); }
  .pm-legend { display: flex; flex-wrap: wrap; gap: 0.35rem 0.85rem; list-style: none; padding: 0; margin: 0; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); }
  .pm-legend li.pm-active { color: var(--text-primary, #fff); }
  .pm-legend strong { color: var(--text-primary, #fff); }
  .pm-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 0.3rem; }
  .pm-readout { margin: 0; min-height: 1.2em; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); }
</style>
